import api from '../api'

// Define types for better type safety
export interface PathParticipant {
  id: string
  employee: string
  created_at: string
  updated_at: string
  started_at: string | null
  completed_at: string | null
  path: string
  current_node: string | null
}

export interface PathParticipantCreateData {
  employee: string
  path: string
}

/**
 * Fetch a list of participants for a path
 */
export const fetchPathParticipantList = async ({
  path_pk,
  page,
  pageSize,
  search,
  ordering,
}: {
  path_pk: string
  page?: number
  pageSize?: number
  search?: string
  ordering?: string
}) => {
  const params: Record<string, any> = {}

  if (page) params.page = page
  if (pageSize) params.page_size = pageSize
  if (search) params.search = search
  if (ordering) params.ordering = ordering

  const res = await api.get(`/path/${path_pk}/participants/`, { params })
  return res.data
}

/**
 * Add a participant to a path
 */
export const createPathParticipant = async (path_pk: string, participantData: PathParticipantCreateData) => {
  const res = await api.post(`/path/${path_pk}/participants/`, participantData)
  return res.data
}

/**
 * Fetch a specific participant from a path
 */
export const fetchPathParticipant = async (path_pk: string, id: string) => {
  const res = await api.get(`/path/${path_pk}/participants/${id}/`)
  return res.data
}

/**
 * Remove a participant from a path
 */
export const deletePathParticipant = async (path_pk: string, id: string) => {
  const res = await api.delete(`/path/${path_pk}/participants/${id}/`)
  return res.data
}

export const fetchParticipationsList = async ({
  has_pending_content,
  has_pending_document,
  has_pending_document_or_content,
  ordering,
  page,
  page_size,
  employee,
  search,
  fields,
  expand,
}: {
  employee?: string | string[]
  has_pending_content?: boolean
  has_pending_document?: boolean
  has_pending_document_or_content?: boolean
  ordering?: string
  page?: number
  page_size?: number
  path?: string
  search?: string
  fields?: string
  expand?: string
}) => {
  const params: Record<string, any> = {
    // default expands include employee and node payloads; callers can override by passing `expand: ''` or a value
    expand: expand === undefined ? 'current_node.content,current_node.document,path,employee,all_nodes.content,all_nodes.template' : (expand === '' ? undefined : expand),
    has_pending_content,
    has_pending_document,
    has_pending_document_or_content,
    ordering,
    page,
    page_size,
    search,
  } as Record<string, any>;
  if (employee) {
    if (Array.isArray(employee)) {
      params['employee__in'] = employee.join(',');
    } else {
      params['employee'] = employee;
    }
  }
  // allow callers to request specific fields to reduce payload
  if (fields) params.fields = fields
  const res = await api.get('/path/participations/', { params });
  return res.data;
}

/**
 * Add a team to a path
 */
export const createPathTeam = async (path_pk: string, teamData: { team: string; path: string }) => {
  const res = await api.post(`/path/${path_pk}/teams/`, teamData)
  return res.data
}

/**
 * Remove a team from a path
 */
export const deletePathTeam = async (path_pk: string, team_id: string) => {
  const res = await api.delete(`/path/${path_pk}/teams/${team_id}/`)
  return res.data
}

/**
 * List teams assigned to a path
 */
export const fetchPathTeams = async (path_pk: string) => {
  const res = await api.get(`/path/${path_pk}/teams/`)
  return res.data
}
